import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Switch, Flex, Button } from "antd";
import styled from "styled-components";
import { colors } from "../../../styles/theme";
import { useBranchContext } from "../../../contexts/branch-context/useBranchContext";

const BranchSection = styled.div`
  margin-bottom: 24px;
`;

const BranchTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const ShowMoreButton = styled(Button)`
  color: ${colors.primary};
  font-size: 14px;
  margin-top: 8px;

  &:hover {
    color: ${colors.primary} !important;
  }
`;

interface ServiceAvailabilityTabProps {
    branchesConfig: Record<number, any>;
    setBranchesConfig: (config: any) => void;
}

export const ServiceAvailabilityTab: React.FC<ServiceAvailabilityTabProps> = ({
    branchesConfig,
    setBranchesConfig,
}) => {
    const { t } = useTranslation();
    const { currentBranch, availableBranches } = useBranchContext();
    const [showAllBranches, setShowAllBranches] = useState(false);

    const updateConfig = (updater: (current: any) => any) => {
        const newConfig = updater(branchesConfig);
        setBranchesConfig(newConfig);
    };

    const toggleBranchVisibility = (branchId: number, value: boolean) => {
        // Zachowujemy strukturę danych z pełną dostępnością w tle
        const defaultSchedules = {
            friday: [{ from: "00:00", till: "23:45" }],
            monday: [{ from: "00:00", till: "23:45" }],
            sunday: [{ from: "00:00", till: "23:45" }],
            tuesday: [{ from: "00:00", till: "23:45" }],
            saturday: [{ from: "00:00", till: "23:45" }],
            thursday: [{ from: "00:00", till: "23:45" }],
            wednesday: [{ from: "00:00", till: "23:45" }],
        };

        updateConfig((current: any) => {
            const currentBranchData = current[branchId] || {
                isVisible: true,
                isAvailable: true,
                availabilitySchedules: defaultSchedules,
            };

            return {
                ...current,
                [branchId]: {
                    ...currentBranchData,
                    isVisible: value,
                    // Gdy usługa jest widoczna, jest też dostępna (uproszczenie)
                    isAvailable: true,
                    availabilitySchedules: currentBranchData.availabilitySchedules || defaultSchedules
                },
            };
        });
    };

    const hasMultipleBranches = availableBranches.length > 1;
    const otherBranches = availableBranches.filter(
        (branch) => branch.id !== currentBranch?.id
    );

    return (
        <>
            {currentBranch && (
                <BranchSection>
                    <BranchTitle>
                        {hasMultipleBranches ? currentBranch.name : t("serviceAvailabilityTab.serviceVisibility")}
                    </BranchTitle>
                    <Flex vertical gap={12}>
                        <Flex align="center" gap={8}>
                            <Switch
                                checked={branchesConfig[currentBranch.id]?.isVisible ?? true}
                                onChange={(value) => toggleBranchVisibility(currentBranch.id, value)}
                            />
                            <span>{t("serviceAvailabilityTab.visibilityInThisBranch")}</span>
                        </Flex>
                    </Flex>
                </BranchSection>
            )}

            {hasMultipleBranches && otherBranches.length > 0 && (
                <>
                    {!showAllBranches ? (
                        <ShowMoreButton
                            type="link"
                            onClick={() => setShowAllBranches(true)}
                        >
                            {t("serviceAvailabilityTab.showOtherBranches")}
                        </ShowMoreButton>
                    ) : (
                        <>
                            {otherBranches
                                .sort((a: any, b: any) => a.id - b.id)
                                .map((branch: any) => (
                                    <BranchSection key={branch.id}>
                                        <BranchTitle>{branch.name}</BranchTitle>
                                        <Flex vertical gap={12}>
                                            <Flex align="center" gap={8}>
                                                <Switch
                                                    checked={branchesConfig[branch.id]?.isVisible ?? true}
                                                    onChange={(value) => toggleBranchVisibility(branch.id, value)}
                                                />
                                                <span>{t("serviceAvailabilityTab.visibilityInThisBranch")}</span>
                                            </Flex>
                                        </Flex>
                                    </BranchSection>
                                ))}
                        </>
                    )}
                </>
            )}
        </>
    );
};
